import { Component } from '@theme/component';

/** @type {string} */
const STORAGE_KEY = 'cb-regional-popup';

/**
 * @typedef {object} Region
 * @property {string} code
 * @property {string} label
 * @property {string} url
 */

/**
 * Home page regional store selector popup.
 *
 * @extends {Component}
 */
class RegionalStorePopup extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#bindRegionButtons();
    this.#bindCloseButton();
    this.#highlightDetectedCountry();

    if (window.Shopify?.designMode) return;
    if (this.dataset.enabled !== 'true') return;
    if (this.#getSavedChoice()) return;

    const dialogComponent = this.querySelector('dialog-component');
    if (!(dialogComponent instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      if (typeof dialogComponent.showDialog === 'function') {
        dialogComponent.showDialog();
      }
    });
  }

  /**
   * @returns {{ region: string } | null}
   */
  #getSavedChoice() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed.region === 'string' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} code
   */
  #saveChoice(code) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ region: code }));
  }

  #bindRegionButtons() {
    this.querySelectorAll('[data-region-code]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.addEventListener('click', () => {
        const code = button.dataset.regionCode || '';
        const url = button.dataset.regionUrl || '';
        this.#handleRegionSelection(code, url);
      });
    });
  }

  #bindCloseButton() {
    const closeButton = this.querySelector('[data-region-dismiss]');
    if (!(closeButton instanceof HTMLButtonElement)) return;

    closeButton.addEventListener('click', () => {
      const code = closeButton.dataset.regionDismiss || 'ZA';
      this.#handleRegionSelection(code, '');
    });
  }

  #highlightDetectedCountry() {
    const liquidCountry = this.dataset.detectedCountry || '';
    const shopifyCountry = window.Shopify?.country || '';
    const detectedCountry = liquidCountry || shopifyCountry;

    if (!detectedCountry) return;

    const highlightCodes = new Set([detectedCountry]);
    if (detectedCountry === 'UK') highlightCodes.add('GB');

    this.querySelectorAll('[data-region-code]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (highlightCodes.has(button.dataset.regionCode || '')) {
        button.classList.add('regional-store-popup__region--suggested');
      }
    });
  }

  /**
   * @param {string} code
   * @param {string} url
   */
  #handleRegionSelection(code, url) {
    this.#saveChoice(code);

    if (code === 'ZA' || !url) {
      const dialogComponent = this.querySelector('dialog-component');
      if (dialogComponent instanceof HTMLElement && typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      }
      return;
    }

    window.location.href = url;
  }
}

if (!customElements.get('regional-store-popup')) {
  customElements.define('regional-store-popup', RegionalStorePopup);
}
